<template>
    <div class="folder-tree-item">
        <div class="folder-tree-item__row"
             :class="{ 'folder-tree-item__row--active': isActive }"
             @click.stop="$emit('select', folder.id)">

            <span class="folder-tree-item__toggle"
                  @click.stop="expanded = !expanded"
                  v-if="hasChildren">
                <i class="material-icons material-icons-round md-18">
                    {{ expanded ? 'expand_more' : 'chevron_right' }}
                </i>
            </span>
            <span class="folder-tree-item__toggle folder-tree-item__toggle--blank" v-else></span>

            <i class="material-icons material-icons-round md-18 folder-tree-item__icon">
                {{ expanded && hasChildren ? 'folder_open' : 'folder' }}
            </i>

            <span class="folder-tree-item__name" v-if="!isEditing">{{ folder.name }}</span>

            <input v-if="isEditing"
                   ref="nameInput"
                   class="folder-tree-item__input"
                   v-model="editName"
                   @keyup.enter="saveName"
                   @keyup.escape="cancelEdit"
                   @blur="saveName" />

            <span class="folder-tree-item__count" v-if="invoiceCount > 0">{{ invoiceCount }}</span>

            <b-dropdown variant="link" size="sm" no-caret right class="folder-tree-item__menu">
                <template slot="button-content">
                    <i class="material-icons material-icons-round md-16">more_vert</i>
                </template>
                <b-dropdown-item @click="startEdit">{{ $t('rename') }}</b-dropdown-item>
                <b-dropdown-item @click="$emit('create-subfolder', folder.id)">{{ $t('new_subfolder') }}</b-dropdown-item>
                <b-dropdown-item @click="$emit('delete', folder.id)" class="text-danger">{{ $t('delete') }}</b-dropdown-item>
            </b-dropdown>
        </div>

        <div v-if="expanded && hasChildren" class="folder-tree-item__children">
            <FolderTreeItem
                v-for="child in children"
                :key="child.id"
                :folder="child"
                :all-folders="allFolders"
                :active-folder-id="activeFolderId"
                :invoice-counts="invoiceCounts"
                @select="$emit('select', $event)"
                @create-subfolder="$emit('create-subfolder', $event)"
                @rename="$emit('rename', $event)"
                @delete="$emit('delete', $event)"
            />
        </div>
    </div>
</template>

<script>
import { BDropdown, BDropdownItem } from 'bootstrap-vue';

export default {
    name: 'FolderTreeItem',
    i18nOptions: { namespaces: 'folders' },
    components: { BDropdown, BDropdownItem },
    props: {
        folder: { type: Object, required: true },
        allFolders: { type: Array, default: () => [] },
        activeFolderId: { type: String, default: null },
        invoiceCounts: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            expanded: false,
            isEditing: false,
            editName: '',
        };
    },
    computed: {
        isActive() {
            return this.activeFolderId === this.folder.id;
        },
        hasChildren() {
            return this.allFolders.some(f => f.parent_id === this.folder.id);
        },
        children() {
            return this.allFolders.filter(f => f.parent_id === this.folder.id);
        },
        invoiceCount() {
            return this.invoiceCounts[this.folder.id] || 0;
        },
    },
    methods: {
        startEdit() {
            this.editName = this.folder.name;
            this.isEditing = true;
            this.$nextTick(() => {
                if (this.$refs.nameInput) this.$refs.nameInput.focus();
            });
        },
        saveName() {
            if (!this.isEditing) return;
            if (this.editName.trim() && this.editName.trim() !== this.folder.name) {
                this.$emit('rename', { id: this.folder.id, name: this.editName.trim() });
            }
            this.isEditing = false;
        },
        cancelEdit() {
            this.isEditing = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.folder-tree-item {
    &__row {
        display: flex;
        align-items: center;
        padding: 4px 8px;
        cursor: pointer;
        border-radius: 4px;
        color: var(--text-primary);
        &:hover {
            background: var(--shade);
        }
    }
    &__row--active {
        background: var(--divider);
        &:hover {
            background: var(--divider);
        }
    }
    &__toggle {
        width: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
        color: var(--text-secondary);
        &--blank {
            visibility: hidden;
        }
    }
    &__icon {
        margin-right: 6px;
        color: var(--text-secondary);
        flex-shrink: 0;
    }
    &__name {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 14px;
        color: var(--text-primary);
    }
    &__input {
        flex: 1;
        border: 1px solid var(--divider);
        background: var(--bg-control);
        color: var(--text-primary);
        border-radius: 3px;
        padding: 1px 6px;
        font-size: 14px;
        outline: none;
        &:focus {
            border-color: var(--primary);
        }
    }
    &__count {
        background: var(--divider);
        border-radius: 10px;
        padding: 0 6px;
        font-size: 11px;
        color: var(--text-secondary);
        margin-left: 6px;
        flex-shrink: 0;
    }
    &__menu {
        margin-left: 4px;
        flex-shrink: 0;
        opacity: 0;
        .folder-tree-item__row:hover & {
            opacity: 1;
        }
    }
    &__children {
        padding-left: 16px;
    }
}
</style>
